import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getPathCrawlResults, runCrawlerPath, getSavedTasks, getJourneysForTask } from "../../../../utils/APIs";
import TableShimmer from "../../../../components/UI/Shimmer/TableShimmer";
import PrimaryModal from "../../../../components/UI/Modal/PrimaryModal";
import { formatDate } from "../../../../utils/converter";
import { toast } from "react-toastify";

const PATH_POLL_INTERVAL = 5000;
const PATH_POLL_TIMEOUT  = 10 * 60 * 1000; // 10 minutes

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDuration = (ms) => {
    if (ms == null) return null;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const m = Math.floor(ms / 60000);
    const s = Math.round((ms % 60000) / 1000);
    return `${m}m ${s}s`;
};

const buildEventGroups = (trackingEvents = [], aaEvents = []) => {
    const aaByRandom = {};
    const nullAA = [];
    for (const ev of aaEvents) {
        if (ev.random != null) (aaByRandom[ev.random] ??= []).push(ev);
        else nullAA.push(ev);
    }
    const usedRandoms = new Set();
    const groups = [];
    for (const ev of trackingEvents) {
        const matched = ev.random != null ? (aaByRandom[ev.random] || []) : [];
        if (ev.random != null) usedRandoms.add(ev.random);
        groups.push({ trackingEvent: ev, aaEvents: matched });
    }
    for (const [random, evs] of Object.entries(aaByRandom)) {
        if (!usedRandoms.has(Number(random)))
            groups.push({ trackingEvent: null, aaEvents: evs });
    }
    if (nullAA.length) groups.push({ trackingEvent: null, aaEvents: nullAA });
    return groups;
};

// ── Compare helpers ───────────────────────────────────────────────────────────

const diffData = (dataA, dataB) => {
    const allKeys = [...new Set([...Object.keys(dataA || {}), ...Object.keys(dataB || {})])].sort();
    return allKeys.map((key) => {
        const a = (dataA || {})[key];
        const b = (dataB || {})[key];
        if (a === undefined) return { key, type: "added",     a: null, b };
        if (b === undefined) return { key, type: "removed",   a,       b: null };
        if (String(a) !== String(b)) return { key, type: "changed",   a,       b };
        return                              { key, type: "unchanged", a,       b };
    });
};

const flattenCaptureGroups = (capture) => {
    const groups = [];
    for (const page of (capture?.data_capture?.pages || [])) {
        const ud = page.utagData || {};
        for (const g of buildEventGroups(ud.trackingEvents || [], ud.adobeAnalyticsEvents || []))
            groups.push(g);
    }
    return groups;
};

// ── Similarity-based event matching ───────────────────────────────────────────

const getGroupName = (g) => g.trackingEvent?.event || g.aaEvents[0]?.event || "unknown";
const getGroupStep = (g) => g.trackingEvent?.step ?? g.aaEvents[0]?.step ?? null;
const getGroupKeys = (g) => new Set([
    ...Object.keys(g.trackingEvent?.data || {}),
    ...g.aaEvents.flatMap((aa) => Object.keys(aa.data || {})),
]);

// Score how well two event groups match (higher = better; -Infinity = incompatible).
// Signals: step proximity (weight 100), Jaccard key overlap (weight 50),
// AA call count alignment (weight ±20/−30).
const scoreGroupPair = (gA, gB) => {
    if (getGroupName(gA) !== getGroupName(gB)) return -Infinity;

    let score = 0;

    const stepA = getGroupStep(gA), stepB = getGroupStep(gB);
    if (stepA !== null && stepB !== null) {
        if (stepA === stepB) score += 100;
        else {
            const diff = Math.abs(stepA - stepB);
            // Credit up to ±3 step drift (e.g. a step was inserted between captures)
            if (diff <= 3) score += Math.max(0, 70 - diff * 20);
            // > 3 apart: no step credit, but don't penalise — key overlap can still save it
        }
    } else if (stepA === null && stepB === null) {
        score += 10; // both stepless — neutral, let key overlap decide
    }

    const keysA = getGroupKeys(gA), keysB = getGroupKeys(gB);
    const unionSize = new Set([...keysA, ...keysB]).size;
    if (unionSize > 0) {
        const intersect = [...keysA].filter((k) => keysB.has(k)).length;
        score += (intersect / unionSize) * 50;
    }

    const aaA = gA.aaEvents.length, aaB = gB.aaEvents.length;
    if (aaA === aaB)                      score += 20;
    else if ((aaA === 0) !== (aaB === 0)) score -= 30; // one has AA calls, the other doesn't

    return score;
};

const COMPARE_MATCH_THRESHOLD = 20;

const buildCompareData = (captureA, captureB) => {
    const groupsA = flattenCaptureGroups(captureA);
    const groupsB = flattenCaptureGroups(captureB);

    // Score every candidate pair and keep those above threshold
    const candidates = [];
    for (let i = 0; i < groupsA.length; i++) {
        for (let j = 0; j < groupsB.length; j++) {
            const score = scoreGroupPair(groupsA[i], groupsB[j]);
            if (score >= COMPARE_MATCH_THRESHOLD) candidates.push({ i, j, score });
        }
    }
    // Process best pairs first (greedy 1-to-1 matching)
    candidates.sort((a, b) => b.score - a.score);

    const matchedA = new Set(), matchedB = new Set();
    const matched = [];
    for (const { i, j } of candidates) {
        if (matchedA.has(i) || matchedB.has(j)) continue;
        matchedA.add(i); matchedB.add(j);
        matched.push({ iA: i, iB: j, groupA: groupsA[i], groupB: groupsB[j] });
    }

    // Sort helpers: order by step then original index so the list reads top-to-bottom naturally
    const orderVal = (step, idx, offset = 0) => (step ?? 1e9) * 10000 + idx * 2 + offset;

    const entries = [
        ...matched.map(({ iA, iB, groupA, groupB }) => ({
            key: `m::${iA}::${iB}`, groupA, groupB,
            _ord: orderVal(getGroupStep(groupA), iA),
        })),
        ...groupsA
            .map((g, i) => matchedA.has(i) ? null : {
                key: `a::${i}`, groupA: g, groupB: null,
                _ord: orderVal(getGroupStep(g), i),
            })
            .filter(Boolean),
        ...groupsB
            .map((g, j) => matchedB.has(j) ? null : {
                key: `b::${j}`, groupA: null, groupB: g,
                _ord: orderVal(getGroupStep(g), j, 1),
            })
            .filter(Boolean),
    ];

    entries.sort((a, b) => a._ord - b._ord);
    return entries.map(({ key, groupA, groupB }) => ({ key, groupA, groupB }));
};

const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    const green = s === "success" || s === "passed" || s === "ok";
    const red   = s === "error"   || s === "failed"  || s === "fail";
    return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
            green ? "bg-green-100 text-green-700" :
            red   ? "bg-red-100 text-red-700"     :
                    "bg-gray-100 text-gray-600"
        }`}>
            {status || "—"}
        </span>
    );
};

const ChevronIcon = ({ open }) => (
    <svg
        className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

const SpinnerIcon = () => (
    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 rounded mt-2">
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
            >
                <span>{title}</span>
                <ChevronIcon open={open} />
            </button>
            {open && <div className="px-3 pb-3">{children}</div>}
        </div>
    );
};

const KeyValueTable = ({ data }) => {
    const entries = Object.entries(data || {});
    if (!entries.length) return <p className="text-xs text-gray-400 italic">No data</p>;
    return (
        <table className="w-full text-xs border-collapse">
            <tbody>
                {entries.map(([key, val], i) => (
                    <tr key={key} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-2 py-1 font-medium text-gray-700 w-1/3 break-all border border-gray-100">{key}</td>
                        <td className="px-2 py-1 text-gray-600 break-all border border-gray-100">
                            {val === null ? <em className="text-gray-400">null</em> : String(val)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const EventDataDiffTable = ({ dataA, dataB, blacklist = [], whitelist = [] }) => {
    const diffs = useMemo(() => diffData(dataA, dataB), [dataA, dataB]);

    const isBlacklisted = (key) => blacklist.some((b) => key === b || key.startsWith(`${b}.`) || b.startsWith(`${key}.`));
    const isWhitelisted = (key) => whitelist.some((w) => key === w || key.startsWith(`${w}.`) || w.startsWith(`${key}.`));

    // Always show all keys (entire event object); exclude blacklisted
    const visible = diffs.filter(({ key }) => !isBlacklisted(key));

    if (!diffs.length) return <p className="text-xs text-gray-400 italic px-1">No data</p>;
    if (!visible.length) return <p className="text-xs text-gray-400 italic px-1">No data (all keys blacklisted).</p>;
    const renderVal = (v) => v == null ? <em className="text-gray-400">null</em> : String(v);
    return (
        <table className="w-full text-xs border-collapse">
            <thead>
                <tr className="bg-gray-50">
                    <th className="px-2 py-1 text-left font-medium text-gray-400 border border-gray-100 w-1/4">Key</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-400 border border-gray-100 w-[37.5%]">A</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-400 border border-gray-100 w-[37.5%]">B</th>
                </tr>
            </thead>
            <tbody>
                {visible.map(({ key, type, a, b }) => {
                    const wl = isWhitelisted(key);
                    const bg = type === "added" ? "bg-green-50" : type === "removed" ? "bg-red-50" : type === "changed" ? "bg-yellow-50" : wl ? "bg-indigo-50" : "";
                    const leftBorder = wl ? { borderLeft: "3px solid #6366f1" } : {};
                    if (type === "unchanged") return (
                        <tr key={key} className={bg} style={leftBorder}>
                            <td className="px-2 py-1 break-all border border-gray-100">
                                <span className="font-medium text-gray-700">{key}</span>
                                {wl && <span className="ml-1 text-[9px] font-semibold uppercase bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded">WL</span>}
                            </td>
                            <td colSpan={2} className="px-2 py-1 text-gray-500 break-all border border-gray-100">{renderVal(a)}</td>
                        </tr>
                    );
                    return (
                        <tr key={key} className={bg} style={leftBorder}>
                            <td className="px-2 py-1 break-all border border-gray-100">
                                <span className="font-medium text-gray-700">{key}</span>
                                {wl && <span className="ml-1 text-[9px] font-semibold uppercase bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded">WL</span>}
                            </td>
                            <td className={`px-2 py-1 break-all border border-gray-100 ${type === "removed" || type === "changed" ? "text-red-700" : "text-gray-300 italic"}`}>
                                {type === "added" ? "—" : renderVal(a)}
                            </td>
                            <td className={`px-2 py-1 break-all border border-gray-100 ${type === "added" || type === "changed" ? "text-green-700" : "text-gray-300 italic"}`}>
                                {type === "removed" ? "—" : renderVal(b)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

const DataLayerTable = ({ data }) => (
    <CollapsibleSection title={`Data Layer (${Object.keys(data || {}).length} keys)`}>
        <KeyValueTable data={data} />
    </CollapsibleSection>
);

const EventGroup = ({ group, stepLog }) => {
    const [open, setOpen] = useState(false);
    const label = group.trackingEvent?.event || group.aaEvents[0]?.event || "Event";
    const aaCount = group.aaEvents.length;
    const stepNum = group.trackingEvent?.step ?? group.aaEvents[0]?.step ?? null;

    const stepByNum = React.useMemo(() => {
        const m = {};
        for (const s of (stepLog || [])) m[s.step] = s;
        return m;
    }, [stepLog]);

    const stepDetail = stepNum != null ? stepByNum[stepNum] : null;
    const stepLabel = stepDetail
        ? [stepDetail.action, stepDetail.selector || stepDetail.url || stepDetail.value || null]
              .filter(Boolean).join(" ")
        : null;

    return (
        <div className="rounded border border-gray-100 overflow-hidden">
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 w-full px-3 py-2 text-left bg-white hover:bg-gray-50"
            >
                {group.trackingEvent ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded flex-shrink-0">Tealium</span>
                ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded flex-shrink-0">AA</span>
                )}
                <span className="text-xs font-medium text-gray-800 truncate flex-1">{label}</span>
                {stepNum != null && (
                    <span
                        className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono flex-shrink-0 max-w-xs truncate"
                        title={stepLabel ? `Step ${stepNum}: ${stepLabel}` : `Step ${stepNum}`}
                    >
                        Step {stepNum}{stepLabel ? `: ${stepLabel}` : ""}
                    </span>
                )}
                {group.trackingEvent && aaCount > 0 && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0 mr-1">→ {aaCount} AA call{aaCount !== 1 ? "s" : ""}</span>
                )}
                {group.trackingEvent && !aaCount && (
                    <span className="text-[10px] text-gray-300 flex-shrink-0 mr-1 italic">no AA match</span>
                )}
                <ChevronIcon open={open} />
            </button>

            {open && (
                <div>
                    {group.trackingEvent && (
                        <div className="border-l-4 border-indigo-400 bg-indigo-50 px-3 py-2">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Tealium</span>
                                {group.trackingEvent.event && (
                                    <span className="text-xs font-semibold text-indigo-900">{group.trackingEvent.event}</span>
                                )}
                            </div>
                            <KeyValueTable data={group.trackingEvent.data || {}} />
                        </div>
                    )}
                    {group.aaEvents.map((aaEv, j) => (
                        <div key={j} className={group.trackingEvent ? "ml-4" : ""}>
                            <div className="border-l-4 border-red-400 bg-red-50 px-3 py-2 border-t border-gray-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded">AA</span>
                                    {aaEv.event && (
                                        <span className="text-xs font-semibold text-red-900">{aaEv.event}</span>
                                    )}
                                    {!group.trackingEvent && (
                                        <span className="text-[10px] text-gray-400 italic ml-auto">no trigger matched</span>
                                    )}
                                </div>
                                <KeyValueTable data={aaEv.data || {}} />
                            </div>
                            {aaEv.utagData && Object.keys(aaEv.utagData).length > 0 && (
                                <div className="ml-4 border-l-4 border-blue-500 bg-blue-50 px-3 py-2 border-t border-gray-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">utag_data</span>
                                        <span className="text-[10px] text-gray-400">{Object.keys(aaEv.utagData).length} keys</span>
                                    </div>
                                    <KeyValueTable data={aaEv.utagData} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const MatchedEventsSection = ({ trackingEvents, adobeAnalyticsEvents, stepLog }) => {
    const groups = useMemo(
        () => buildEventGroups(trackingEvents, adobeAnalyticsEvents),
        [trackingEvents, adobeAnalyticsEvents]
    );

    // keyFilters: [{ key: string, values: string[], pendingValue: string }]
    const [filterEventType,  setFilterEventType]  = useState("");
    const [filterStepAction, setFilterStepAction] = useState("");
    const [keyFilters,       setKeyFilters]       = useState([]);
    const [addKeySelect,     setAddKeySelect]     = useState("");
    const [filterStep,       setFilterStep]       = useState("");

    const eventTypes = useMemo(() => {
        const s = new Set();
        for (const g of groups) {
            if (g.trackingEvent?.event) s.add(g.trackingEvent.event);
            for (const aa of g.aaEvents) if (aa.event) s.add(aa.event);
        }
        return [...s].sort();
    }, [groups]);

    const allKeys = useMemo(() => {
        const s = new Set();
        for (const g of groups) {
            for (const k of Object.keys(g.trackingEvent?.data || {})) s.add(k);
            for (const aa of g.aaEvents) for (const k of Object.keys(aa.data || {})) s.add(k);
        }
        return [...s].sort();
    }, [groups]);

    const allSteps = useMemo(() => {
        const s = new Set();
        for (const g of groups) {
            if (g.trackingEvent?.step != null) s.add(g.trackingEvent.step);
            for (const aa of g.aaEvents) if (aa.step != null) s.add(aa.step);
        }
        return [...s].sort((a, b) => a - b);
    }, [groups]);

    // Map step number → action for fast lookup during filtering
    const stepActionMap = useMemo(() => {
        const m = {};
        for (const s of (stepLog || [])) m[s.step] = s.action;
        return m;
    }, [stepLog]);

    const stepActionTypes = useMemo(() => {
        const s = new Set(Object.values(stepActionMap));
        return [...s].sort();
    }, [stepActionMap]);

    const availableKeys = useMemo(
        () => allKeys.filter((k) => !keyFilters.some((f) => f.key === k)),
        [allKeys, keyFilters]
    );

    const addKeyFilter = (key) => {
        if (!key) return;
        setKeyFilters((prev) => [...prev, { key, values: [], pendingValue: "" }]);
        setAddKeySelect("");
    };

    const removeKeyFilter = (key) =>
        setKeyFilters((prev) => prev.filter((f) => f.key !== key));

    const updatePending = (key, val) =>
        setKeyFilters((prev) => prev.map((f) => f.key === key ? { ...f, pendingValue: val } : f));

    const commitValue = (key) =>
        setKeyFilters((prev) => prev.map((f) => {
            if (f.key !== key || !f.pendingValue.trim()) return f;
            const trimmed = f.pendingValue.trim();
            if (f.values.includes(trimmed)) return { ...f, pendingValue: "" };
            return { ...f, values: [...f.values, trimmed], pendingValue: "" };
        }));

    const removeValue = (key, val) =>
        setKeyFilters((prev) => prev.map((f) =>
            f.key === key ? { ...f, values: f.values.filter((v) => v !== val) } : f
        ));

    const getGroupValsForKey = (g, key) => {
        const vals = [];
        const tv = g.trackingEvent?.data?.[key];
        if (tv != null) vals.push(String(tv));
        for (const aa of g.aaEvents) { const v = aa.data?.[key]; if (v != null) vals.push(String(v)); }
        return vals;
    };

    const filteredGroups = useMemo(() => {
        return groups.filter((g) => {
            if (filterEventType) {
                const match = g.trackingEvent?.event === filterEventType ||
                    g.aaEvents.some((aa) => aa.event === filterEventType);
                if (!match) return false;
            }
            for (const { key, values } of keyFilters) {
                const eventVals = getGroupValsForKey(g, key);
                if (!eventVals.length) return false;
                if (values.length > 0) {
                    const anyMatch = values.some((fv) =>
                        eventVals.some((ev) => ev.toLowerCase().includes(fv.toLowerCase()))
                    );
                    if (!anyMatch) return false;
                }
            }
            if (filterStepAction) {
                const eventStep = g.trackingEvent?.step ?? g.aaEvents[0]?.step ?? null;
                if (eventStep == null || stepActionMap[eventStep] !== filterStepAction) return false;
            }
            if (filterStep !== "") {
                const stepN = Number(filterStep);
                const match = g.trackingEvent?.step === stepN ||
                    g.aaEvents.some((aa) => aa.step === stepN);
                if (!match) return false;
            }
            return true;
        });
    }, [groups, filterEventType, filterStepAction, keyFilters, filterStep, stepActionMap]);

    if (!groups.length) return null;

    const totalTracking = (trackingEvents || []).length;
    const totalAA       = (adobeAnalyticsEvents || []).length;
    const title = [
        totalTracking ? `${totalTracking} trigger${totalTracking !== 1 ? "s" : ""}` : null,
        totalAA       ? `${totalAA} analytics call${totalAA !== 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(", ");

    const selectCls = "text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary";
    const hasFilters = filterEventType || filterStepAction || keyFilters.length > 0 || filterStep !== "";

    return (
        <CollapsibleSection title={`Events (${title || "none"})`}>
            {/* Filter bar */}
            <div className="mt-2 mb-3 pb-3 border-b border-gray-100 space-y-2">
                {/* Row 1: event type + step action + step + clear */}
                <div className="flex flex-wrap items-center gap-2">
                    <select value={filterEventType} onChange={(e) => setFilterEventType(e.target.value)} className={selectCls}>
                        <option value="">All event types</option>
                        {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select value={filterStepAction} onChange={(e) => setFilterStepAction(e.target.value)} className={selectCls}>
                        <option value="">All actions</option>
                        {stepActionTypes.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>

                    <select value={filterStep} onChange={(e) => setFilterStep(e.target.value)} className={selectCls}>
                        <option value="">All steps</option>
                        {allSteps.map((s) => <option key={s} value={s}>Step {s}</option>)}
                    </select>

                    {hasFilters && (
                        <button
                            onClick={() => { setFilterEventType(""); setFilterStepAction(""); setKeyFilters([]); setAddKeySelect(""); setFilterStep(""); }}
                            className="text-xs text-gray-400 hover:text-gray-600 px-1"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                {/* Active key filters */}
                {keyFilters.map(({ key, values, pendingValue }) => (
                    <div key={key} className="flex flex-wrap items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                        <span className="text-[10px] font-semibold text-gray-600 font-mono mr-1 flex-shrink-0">{key}</span>

                        {values.map((v) => (
                            <span key={v} className="flex items-center gap-0.5 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                                {v}
                                <button onClick={() => removeValue(key, v)} className="ml-0.5 hover:text-indigo-900 leading-none">×</button>
                            </span>
                        ))}

                        <input
                            type="text"
                            value={pendingValue}
                            onChange={(e) => updatePending(key, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitValue(key); } }}
                            placeholder="Add value… (Enter)"
                            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary min-w-28 flex-1"
                        />

                        <button onClick={() => removeKeyFilter(key)} className="text-gray-300 hover:text-red-400 ml-auto text-sm leading-none flex-shrink-0">×</button>
                    </div>
                ))}

                {/* Add key filter */}
                {availableKeys.length > 0 && (
                    <select
                        value={addKeySelect}
                        onChange={(e) => addKeyFilter(e.target.value)}
                        className={selectCls}
                    >
                        <option value="">+ Filter by key…</option>
                        {availableKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                )}
            </div>

            <div className="space-y-2">
                {filteredGroups.length ? (
                    filteredGroups.map((group, i) => (
                        <EventGroup key={i} group={group} stepLog={stepLog} />
                    ))
                ) : (
                    <p className="text-xs text-gray-400 italic text-center py-3">No events match the current filters.</p>
                )}
            </div>
        </CollapsibleSection>
    );
};

const StepLogTable = ({ stepLog }) => {
    if (!stepLog?.length) {
        return <p className="text-xs text-gray-400 italic mt-2">No step log available.</p>;
    }
    return (
        <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr className="bg-gray-100 text-left">
                        <th className="px-3 py-2 border border-gray-200 font-medium text-gray-700">#</th>
                        <th className="px-3 py-2 border border-gray-200 font-medium text-gray-700">Action</th>
                        <th className="px-3 py-2 border border-gray-200 font-medium text-gray-700">Selector / Value</th>
                        <th className="px-3 py-2 border border-gray-200 font-medium text-gray-700">Status</th>
                        <th className="px-3 py-2 border border-gray-200 font-medium text-gray-700 text-right">Duration</th>
                    </tr>
                </thead>
                <tbody>
                    {stepLog.map((row, i) => {
                        const failed = row.status === "error";
                        const rowCls = failed ? "bg-red-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50";
                        return (
                            <React.Fragment key={i}>
                                <tr className={rowCls}>
                                    <td className="px-3 py-1.5 border border-gray-100">{row.step ?? i + 1}</td>
                                    <td className="px-3 py-1.5 border border-gray-100 font-mono">{row.action || "—"}</td>
                                    <td className="px-3 py-1.5 border border-gray-100 break-all text-gray-500">
                                        {row.selector || row.url || row.value || "—"}
                                    </td>
                                    <td className="px-3 py-1.5 border border-gray-100">{statusBadge(row.status)}</td>
                                    <td className="px-3 py-1.5 border border-gray-100 text-right font-mono text-gray-500 whitespace-nowrap">
                                        {formatDuration(row.durationMs) ?? "—"}
                                    </td>
                                </tr>
                                {failed && row.error && (
                                    <tr className="bg-red-50">
                                        <td colSpan={5} className="px-3 py-2 border border-red-100">
                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-red-500 mb-1">Error detail</p>
                                            <pre className="text-xs text-red-700 whitespace-pre-wrap break-all font-mono leading-relaxed">{row.error}</pre>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const PageCaptureCard = ({ page, index, pageSteps, stepLog, onScreenshotClick }) => {
    const ud = page.utagData || {};
    const screenshotSrc = page.screenshotBase64
        ? `data:image/png;base64,${page.screenshotBase64}`
        : null;
    const goToStep = pageSteps?.[0];

    return (
        <div className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-start gap-3">
                {screenshotSrc && (
                    <img
                        src={screenshotSrc}
                        alt={`Page ${index} screenshot`}
                        className="max-h-24 w-auto object-contain rounded border border-gray-200 cursor-pointer flex-shrink-0 hover:opacity-80 transition-opacity"
                        onClick={() => onScreenshotClick(screenshotSrc, `Page ${index} — ${page.url || ""}`)}
                        title="Click to expand"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-xs font-semibold text-gray-700">Page {index}</p>
                        {goToStep && (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                                Step {goToStep.step ?? index}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 break-all">{page.url || "Unknown URL"}</p>
                </div>
            </div>
            <div className="mt-2">
                <DataLayerTable data={ud.defaultDataLayer} />
                <MatchedEventsSection
                    trackingEvents={ud.trackingEvents}
                    adobeAnalyticsEvents={ud.adobeAnalyticsEvents}
                    stepLog={stepLog}
                />
            </div>
        </div>
    );
};

const CompareEventGroup = ({ groupA, groupB, status, showUnchanged, stepLog, blacklist = [], whitelist = [] }) => {
    const [open, setOpen] = useState(false);
    const group = groupA || groupB;
    const label = group.trackingEvent?.event || group.aaEvents[0]?.event || "Event";
    const stepNum = group.trackingEvent?.step ?? group.aaEvents[0]?.step ?? null;

    const stepByNum = useMemo(() => {
        const m = {};
        for (const s of (stepLog || [])) m[s.step] = s;
        return m;
    }, [stepLog]);

    const stepDetail = stepNum != null ? stepByNum[stepNum] : null;
    const stepLabel  = stepDetail
        ? [stepDetail.action, stepDetail.selector || stepDetail.url || stepDetail.value || null]
              .filter(Boolean).join(" ")
        : null;

    const borderCls = status === "added" ? "border-green-200" : status === "removed" ? "border-red-200" : status === "changed" ? "border-yellow-200" : "border-gray-100";
    const headerBg  = status === "added" ? "bg-green-50 hover:bg-green-100" : status === "removed" ? "bg-red-50 hover:bg-red-100" : status === "changed" ? "bg-yellow-50 hover:bg-yellow-100" : "bg-white hover:bg-gray-50";
    const pill = status === "added"   ? <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">added</span>
               : status === "removed" ? <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">removed</span>
               : status === "changed" ? <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">changed</span>
               : <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">unchanged</span>;

    return (
        <div className={`rounded border overflow-hidden ${borderCls}`}>
            <button onClick={() => setOpen((o) => !o)} className={`flex items-center gap-2 w-full px-3 py-2 text-left ${headerBg}`}>
                <span className="text-xs font-medium text-gray-800 truncate flex-1">{label}</span>
                {stepNum != null && (
                    <span
                        className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono flex-shrink-0 max-w-xs truncate"
                        title={stepLabel ? `Step ${stepNum}: ${stepLabel}` : `Step ${stepNum}`}
                    >
                        Step {stepNum}{stepLabel ? `: ${stepLabel}` : ""}
                    </span>
                )}
                {pill}
                <ChevronIcon open={open} />
            </button>
            {open && (
                <div className="border-t border-gray-100">
                    {(groupA?.trackingEvent || groupB?.trackingEvent) && (
                        <div className="px-3 py-2 border-l-4 border-indigo-300">
                            <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded mb-2 inline-block">Tealium</span>
                            {status === "added" || status === "removed"
                                ? <KeyValueTable data={group.trackingEvent?.data || {}} />
                                : <EventDataDiffTable dataA={groupA?.trackingEvent?.data} dataB={groupB?.trackingEvent?.data} blacklist={blacklist} whitelist={whitelist} />
                            }
                        </div>
                    )}
                    {(() => {
                        const maxAA = Math.max((groupA?.aaEvents || []).length, (groupB?.aaEvents || []).length);
                        if (!maxAA) return null;
                        return Array.from({ length: maxAA }, (_, j) => {
                            const aaA = groupA?.aaEvents[j];
                            const aaB = groupB?.aaEvents[j];
                            const aaName = (aaA || aaB)?.event || "";
                            return (
                                <div key={j} className="px-3 py-2 border-t border-gray-100 ml-4 border-l-4 border-red-300">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded">AA</span>
                                        {aaName && <span className="text-xs text-red-900 font-semibold">{aaName}</span>}
                                        {!aaA && <span className="text-[10px] text-green-600 italic ml-auto">aa call added</span>}
                                        {!aaB && <span className="text-[10px] text-red-600 italic ml-auto">aa call removed</span>}
                                    </div>
                                    {!aaA || !aaB
                                        ? <KeyValueTable data={(aaA || aaB)?.data || {}} />
                                        : <EventDataDiffTable dataA={aaA?.data} dataB={aaB?.data} blacklist={blacklist} whitelist={whitelist} />
                                    }
                                </div>
                            );
                        });
                    })()}
                </div>
            )}
        </div>
    );
};

const CrawlCompareView = ({ captureA, captureB, blacklist = [], whitelist = [] }) => {
    const [showUnchanged,  setShowUnchanged]  = useState(false);
    const [filterEventType,  setFilterEventType]  = useState("");
    const [filterStepAction, setFilterStepAction] = useState("");
    const [keyFilters,       setKeyFilters]       = useState([]);
    const [addKeySelect,     setAddKeySelect]     = useState("");
    const [filterStep,       setFilterStep]       = useState("");

    const compareData = useMemo(() => buildCompareData(captureA, captureB), [captureA, captureB]);

    const classified = useMemo(() => {
        const isBL = (key) => blacklist.some(
            (b) => key === b || key.startsWith(`${b}.`) || b.startsWith(`${key}.`)
        );
        return compareData.map((entry) => {
            const { groupA, groupB } = entry;
            if (!groupA) return { ...entry, status: "added" };
            if (!groupB) return { ...entry, status: "removed" };
            const tDiffs = diffData(groupA.trackingEvent?.data, groupB.trackingEvent?.data)
                .filter(({ key }) => !isBL(key));
            let hasDiffs = tDiffs.some((d) => d.type !== "unchanged");
            if (!hasDiffs) {
                if (groupA.aaEvents.length !== groupB.aaEvents.length) hasDiffs = true;
                else {
                    for (let i = 0; i < groupA.aaEvents.length && !hasDiffs; i++)
                        hasDiffs = diffData(groupA.aaEvents[i]?.data, groupB.aaEvents[i]?.data)
                            .filter(({ key }) => !isBL(key))
                            .some((d) => d.type !== "unchanged");
                }
            }
            return { ...entry, status: hasDiffs ? "changed" : "unchanged" };
        });
    }, [compareData, blacklist]);

    // Combined step log from both captures (B takes precedence for same step)
    const combinedStepLog = useMemo(() => {
        const map = {};
        for (const s of (captureA?.data_capture?.stepLog || [])) map[s.step] = s;
        for (const s of (captureB?.data_capture?.stepLog || [])) map[s.step] = s;
        return Object.values(map);
    }, [captureA, captureB]);

    const stepActionMap = useMemo(() => {
        const m = {};
        for (const s of combinedStepLog) m[s.step] = s.action;
        return m;
    }, [combinedStepLog]);

    // Derive filter options from both groups of every entry
    const eventTypes = useMemo(() => {
        const s = new Set();
        for (const { groupA, groupB } of classified) {
            for (const g of [groupA, groupB].filter(Boolean)) {
                if (g.trackingEvent?.event) s.add(g.trackingEvent.event);
                for (const aa of g.aaEvents) if (aa.event) s.add(aa.event);
            }
        }
        return [...s].sort();
    }, [classified]);

    const allKeys = useMemo(() => {
        const s = new Set();
        for (const { groupA, groupB } of classified) {
            for (const g of [groupA, groupB].filter(Boolean)) {
                for (const k of Object.keys(g.trackingEvent?.data || {})) s.add(k);
                for (const aa of g.aaEvents) for (const k of Object.keys(aa.data || {})) s.add(k);
            }
        }
        return [...s].sort();
    }, [classified]);

    const allSteps = useMemo(() => {
        const s = new Set();
        for (const { groupA, groupB } of classified) {
            for (const g of [groupA, groupB].filter(Boolean)) {
                if (g.trackingEvent?.step != null) s.add(g.trackingEvent.step);
                for (const aa of g.aaEvents) if (aa.step != null) s.add(aa.step);
            }
        }
        return [...s].sort((a, b) => a - b);
    }, [classified]);

    const stepActionTypes = useMemo(() => [...new Set(Object.values(stepActionMap))].sort(), [stepActionMap]);
    const availableKeys   = useMemo(() => allKeys.filter((k) => !keyFilters.some((f) => f.key === k)), [allKeys, keyFilters]);

    const addKeyFilter  = (key) => { if (!key) return; setKeyFilters((p) => [...p, { key, values: [], pendingValue: "" }]); setAddKeySelect(""); };
    const removeKeyFilter = (key) => setKeyFilters((p) => p.filter((f) => f.key !== key));
    const updatePending   = (key, val) => setKeyFilters((p) => p.map((f) => f.key === key ? { ...f, pendingValue: val } : f));
    const commitValue     = (key) => setKeyFilters((p) => p.map((f) => {
        if (f.key !== key || !f.pendingValue.trim()) return f;
        const t = f.pendingValue.trim();
        return f.values.includes(t) ? { ...f, pendingValue: "" } : { ...f, values: [...f.values, t], pendingValue: "" };
    }));
    const removeValue = (key, val) => setKeyFilters((p) => p.map((f) => f.key === key ? { ...f, values: f.values.filter((v) => v !== val) } : f));

    // Check both groupA and groupB for a key's values
    const getEntryValsForKey = (entry, key) => {
        const vals = [];
        for (const g of [entry.groupA, entry.groupB].filter(Boolean)) {
            const tv = g.trackingEvent?.data?.[key];
            if (tv != null) vals.push(String(tv));
            for (const aa of g.aaEvents) { const v = aa.data?.[key]; if (v != null) vals.push(String(v)); }
        }
        return vals;
    };

    const stats = useMemo(() => ({
        added:     classified.filter((e) => e.status === "added").length,
        removed:   classified.filter((e) => e.status === "removed").length,
        changed:   classified.filter((e) => e.status === "changed").length,
        unchanged: classified.filter((e) => e.status === "unchanged").length,
    }), [classified]);

    const sortedEntries = useMemo(() => {
        const order = { changed: 0, added: 1, removed: 2, unchanged: 3 };
        return [...classified].sort((a, b) => order[a.status] - order[b.status]);
    }, [classified]);

    const filteredEntries = useMemo(() => {
        const base = showUnchanged ? sortedEntries : sortedEntries.filter((e) => e.status !== "unchanged");
        return base.filter((entry) => {
            if (filterEventType) {
                const inA = entry.groupA?.trackingEvent?.event === filterEventType || entry.groupA?.aaEvents.some((aa) => aa.event === filterEventType);
                const inB = entry.groupB?.trackingEvent?.event === filterEventType || entry.groupB?.aaEvents.some((aa) => aa.event === filterEventType);
                if (!inA && !inB) return false;
            }
            for (const { key, values } of keyFilters) {
                const entryVals = getEntryValsForKey(entry, key);
                if (!entryVals.length) return false;
                if (values.length > 0) {
                    const anyMatch = values.some((fv) => entryVals.some((ev) => ev.toLowerCase().includes(fv.toLowerCase())));
                    if (!anyMatch) return false;
                }
            }
            if (filterStepAction) {
                const stepA = entry.groupA?.trackingEvent?.step ?? entry.groupA?.aaEvents[0]?.step ?? null;
                const stepB = entry.groupB?.trackingEvent?.step ?? entry.groupB?.aaEvents[0]?.step ?? null;
                const matchA = stepA != null && stepActionMap[stepA] === filterStepAction;
                const matchB = stepB != null && stepActionMap[stepB] === filterStepAction;
                if (!matchA && !matchB) return false;
            }
            if (filterStep !== "") {
                const stepN = Number(filterStep);
                const inA = entry.groupA?.trackingEvent?.step === stepN || entry.groupA?.aaEvents.some((aa) => aa.step === stepN);
                const inB = entry.groupB?.trackingEvent?.step === stepN || entry.groupB?.aaEvents.some((aa) => aa.step === stepN);
                if (!inA && !inB) return false;
            }
            return true;
        });
    }, [sortedEntries, showUnchanged, filterEventType, keyFilters, filterStepAction, filterStep, stepActionMap]);

    const hasFilters = filterEventType || filterStepAction || keyFilters.length > 0 || filterStep !== "";
    const clearFilters = () => { setFilterEventType(""); setFilterStepAction(""); setKeyFilters([]); setAddKeySelect(""); setFilterStep(""); };

    const selectCls = "text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary";

    return (
        <div>
            {/* Summary pills + show unchanged toggle */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {stats.changed > 0 && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">{stats.changed} changed</span>}
                {stats.added   > 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{stats.added} added</span>}
                {stats.removed > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">{stats.removed} removed</span>}
                {stats.unchanged > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{stats.unchanged} unchanged</span>}
                <label className="flex items-center gap-1.5 text-xs text-gray-500 ml-auto cursor-pointer select-none">
                    <input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} className="rounded" />
                    Show unchanged
                </label>
            </div>

            {/* Filter bar */}
            <div className="mb-3 pb-3 border-b border-gray-100 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <select value={filterEventType} onChange={(e) => setFilterEventType(e.target.value)} className={selectCls}>
                        <option value="">All event types</option>
                        {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={filterStepAction} onChange={(e) => setFilterStepAction(e.target.value)} className={selectCls}>
                        <option value="">All actions</option>
                        {stepActionTypes.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={filterStep} onChange={(e) => setFilterStep(e.target.value)} className={selectCls}>
                        <option value="">All steps</option>
                        {allSteps.map((s) => <option key={s} value={s}>Step {s}</option>)}
                    </select>
                    {hasFilters && (
                        <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 px-1">Clear all</button>
                    )}
                </div>

                {keyFilters.map(({ key, values, pendingValue }) => (
                    <div key={key} className="flex flex-wrap items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                        <span className="text-[10px] font-semibold text-gray-600 font-mono mr-1 flex-shrink-0">{key}</span>
                        {values.map((v) => (
                            <span key={v} className="flex items-center gap-0.5 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                                {v}
                                <button onClick={() => removeValue(key, v)} className="ml-0.5 hover:text-indigo-900 leading-none">×</button>
                            </span>
                        ))}
                        <input
                            type="text" value={pendingValue}
                            onChange={(e) => updatePending(key, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitValue(key); } }}
                            placeholder="Add value… (Enter)"
                            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary min-w-28 flex-1"
                        />
                        <button onClick={() => removeKeyFilter(key)} className="text-gray-300 hover:text-red-400 ml-auto text-sm leading-none flex-shrink-0">×</button>
                    </div>
                ))}

                {availableKeys.length > 0 && (
                    <select value={addKeySelect} onChange={(e) => addKeyFilter(e.target.value)} className={selectCls}>
                        <option value="">+ Filter by key…</option>
                        {availableKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                )}
            </div>

            <div className="space-y-2">
                {filteredEntries.length ? filteredEntries.map((entry) => (
                    <CompareEventGroup
                        key={entry.key}
                        groupA={entry.groupA}
                        groupB={entry.groupB}
                        status={entry.status}
                        showUnchanged={showUnchanged}
                        stepLog={combinedStepLog}
                        blacklist={blacklist}
                        whitelist={whitelist}
                    />
                )) : (
                    <p className="text-xs text-gray-400 italic text-center py-4 border border-dashed border-gray-200 rounded">
                        {hasFilters
                            ? "No events match the current filters."
                            : stats.unchanged > 0
                                ? "No changes detected. Enable \"Show unchanged\" to view all events."
                                : "No events to compare."}
                    </p>
                )}
            </div>
        </div>
    );
};

const CaptureSelector = ({ dataCollections, selectedIdx, onChange }) => {
    if (!dataCollections?.length) {
        return <p className="text-xs text-gray-400 italic">No captures yet — run this path to see results.</p>;
    }
    return (
        <div className="flex items-center gap-2 mt-2 mb-3">
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Capture date:</label>
            <select
                value={selectedIdx}
                onChange={(e) => onChange(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
            >
                {dataCollections.map((col, i) => (
                    <option key={i} value={i}>
                        {col.date_captured ? formatDate(col.date_captured) : `Capture ${i + 1}`}
                    </option>
                ))}
            </select>
        </div>
    );
};

const PathCard = ({ path, pathDoc, taskId, token, organizationId, onRunComplete, onScreenshotClick, blacklist = [], whitelist = [] }) => {
    const [running, setRunning]                 = useState(false);
    const [selectedCaptureIdx, setSelectedCaptureIdx] = useState(0);
    const [compareMode,   setCompareMode]   = useState(false);
    const [compareIdxA,   setCompareIdxA]   = useState(1);
    const [compareIdxB,   setCompareIdxB]   = useState(0);
    const pollRef    = useRef(null);
    const pollStart  = useRef(null);

    // Sort captures newest-first
    const sortedCollections = React.useMemo(() => {
        if (!pathDoc?.data_collections?.length) return [];
        return [...pathDoc.data_collections].sort(
            (a, b) => new Date(b.date_captured) - new Date(a.date_captured)
        );
    }, [pathDoc]);

    const selectedCapture = sortedCollections[selectedCaptureIdx] ?? null;

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const startPolling = useCallback((prevCount) => {
        stopPolling();
        pollStart.current = Date.now();
        pollRef.current = setInterval(async () => {
            if (Date.now() - pollStart.current > PATH_POLL_TIMEOUT) {
                stopPolling();
                setRunning(false);
                toast.error(`Path "${path.name}" timed out. Check the task logs for details.`);
                return;
            }
            try {
                const res = await getPathCrawlResults(token, organizationId, taskId);
                const allDocs = Array.isArray(res?.data) ? res.data : [];
                const updatedDoc = allDocs.find((d) => d.journey_id?.toString() === path._id?.toString());
                const newCount = updatedDoc?.data_collections?.length ?? 0;
                if (newCount > prevCount) {
                    stopPolling();
                    setRunning(false);
                    setSelectedCaptureIdx(0);
                    toast.success(`Path "${path.name}" completed.`);
                    onRunComplete(allDocs);
                }
            } catch {
                stopPolling();
                setRunning(false);
                toast.error(`Polling failed for path "${path.name}".`);
            }
        }, PATH_POLL_INTERVAL);
    }, [path._id, path.name, token, organizationId, taskId, onRunComplete, stopPolling]);

    const handleRunPath = async () => {
        const prevCount = pathDoc?.data_collections?.length ?? 0;
        setRunning(true);
        try {
            await runCrawlerPath(token, organizationId, taskId, path._id);
            startPolling(prevCount);
        } catch (err) {
            toast.error(err?.message || `Failed to start path "${path.name}".`);
            setRunning(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h3 className="font-medium text-gray-900">{path.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {path.instructions?.length ?? 0} step{path.instructions?.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {sortedCollections.length >= 2 && (
                        <button
                            onClick={() => setCompareMode((m) => !m)}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                compareMode
                                    ? "text-primary bg-primary/10 hover:bg-primary/20"
                                    : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                            }`}
                        >
                            {compareMode ? "← Back" : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Compare
                                </>
                            )}
                        </button>
                    )}
                    <button
                        onClick={handleRunPath}
                        disabled={running}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    >
                        {running ? <><SpinnerIcon /> Running...</> : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Run Path
                            </>
                        )}
                    </button>
                </div>
            </div>

            {sortedCollections.length > 0 ? (
                <>
                    {compareMode ? (
                        <>
                            {/* Two capture selectors for A/B */}
                            <div className="flex flex-wrap items-center gap-4 mt-2 mb-4 pb-3 border-b border-gray-100">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Baseline (A):</label>
                                    <select
                                        value={compareIdxA}
                                        onChange={(e) => setCompareIdxA(Number(e.target.value))}
                                        className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                                    >
                                        {sortedCollections.map((col, i) => (
                                            <option key={i} value={i}>
                                                {col.date_captured ? formatDate(col.date_captured) : `Capture ${i + 1}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Compare with (B):</label>
                                    <select
                                        value={compareIdxB}
                                        onChange={(e) => setCompareIdxB(Number(e.target.value))}
                                        className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                                    >
                                        {sortedCollections.map((col, i) => (
                                            <option key={i} value={i}>
                                                {col.date_captured ? formatDate(col.date_captured) : `Capture ${i + 1}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <CrawlCompareView
                                captureA={sortedCollections[compareIdxA]}
                                captureB={sortedCollections[compareIdxB]}
                                blacklist={blacklist}
                                whitelist={whitelist}
                            />
                        </>
                    ) : (
                    <>
                    <CaptureSelector
                        dataCollections={sortedCollections}
                        selectedIdx={selectedCaptureIdx}
                        onChange={setSelectedCaptureIdx}
                    />
                    {selectedCapture && (
                        <>
                            {selectedCapture.data_capture?.failed && (() => {
                                const { step, action, error } = selectedCapture.data_capture.failed;
                                return (
                                    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                            </svg>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-red-700">
                                                    Crawl failed at Step {step}
                                                    {action ? <span className="font-normal text-red-600"> · {action}</span> : null}
                                                </p>
                                                <p className="text-xs text-red-500 mt-0.5">Events captured before the failure are shown below.</p>
                                                <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap break-all font-mono bg-red-100 rounded p-2 leading-relaxed">{error}</pre>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            <CollapsibleSection title={(() => {
                                const n      = selectedCapture.data_capture?.stepLog?.length ?? 0;
                                const dur    = formatDuration(selectedCapture.data_capture?.totalDurationMs);
                                const failed = selectedCapture.data_capture?.failed;
                                const base   = `Step Log (${n} step${n !== 1 ? "s" : ""}${dur ? ` · ${dur}` : ""})`;
                                return failed ? `${base} — failed at step ${failed.step}` : base;
                            })()} defaultOpen={false}>
                                <StepLogTable stepLog={selectedCapture.data_capture?.stepLog} />
                            </CollapsibleSection>
                            <div className="mt-3 space-y-3">
                                {(() => {
                                    const stepLog = selectedCapture.data_capture?.stepLog || [];
                                    // Build index boundaries: each go_to step starts a new page capture
                                    const goToIdxs = stepLog.reduce((acc, s, idx) => {
                                        if (s.action === "go_to") acc.push(idx);
                                        return acc;
                                    }, []);
                                    return (selectedCapture.data_capture?.pages || []).map((page, i) => {
                                        const from = goToIdxs[i] ?? null;
                                        const to   = goToIdxs[i + 1] ?? stepLog.length;
                                        const pageSteps = from != null ? stepLog.slice(from, to) : [];
                                        return (
                                            <PageCaptureCard
                                                key={`${path._id}-page-${i}`}
                                                page={page}
                                                index={i + 1}
                                                pageSteps={pageSteps}
                                                stepLog={stepLog}
                                                onScreenshotClick={onScreenshotClick}
                                            />
                                        );
                                    });
                                })()}
                                {!selectedCapture.data_capture?.pages?.length && (
                                    <p className="text-xs text-gray-400 italic text-center py-4 border border-dashed border-gray-200 rounded">
                                        No page captures in this run.
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    ) : (
        <p className="text-sm text-gray-400 italic text-center py-6 border border-dashed border-gray-200 rounded">
            No captures yet — run this path to see results.
        </p>
    )}
        </div>
    );
}; // PathCard

// ── Main export ───────────────────────────────────────────────────────────────

const PathCrawlsTab = ({ taskId, token, organizationId }) => {
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);
    const [paths,    setPaths]    = useState([]);
    const [pathDocs, setPathDocs] = useState([]);
    const [screenshotModal, setScreenshotModal] = useState({ open: false, src: "", label: "" });
    const [varBlacklist, setVarBlacklist] = useState([]);
    const [varWhitelist, setVarWhitelist] = useState([]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [pathsRes, docsRes, taskRes] = await Promise.all([
                    getJourneysForTask(token, organizationId, taskId),
                    getPathCrawlResults(token, organizationId, taskId),
                    getSavedTasks(token, organizationId, taskId),
                ]);
                setPaths(Array.isArray(pathsRes?.data) ? pathsRes.data : []);
                setPathDocs(Array.isArray(docsRes?.data) ? docsRes.data : []);
                const task = Array.isArray(taskRes?.data) ? taskRes.data[0] : taskRes?.data;
                if (task) {
                    setVarBlacklist(task.variable_name_blacklist ?? []);
                    setVarWhitelist(task.variable_name_whitelist ?? []);
                }
            } catch (err) {
                setError(err?.message || "Failed to load path crawl data.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [taskId, token, organizationId]);

    const handleRunComplete = useCallback((allDocs) => {
        setPathDocs(allDocs);
    }, []);

    const handleScreenshotClick = useCallback((src, label) => {
        setScreenshotModal({ open: true, src, label });
    }, []);

    if (loading) return <div className="w-full"><TableShimmer colSize={3} rowSize={4} /></div>;

    if (error) return (
        <p className="w-full text-center text-red-500 mt-6">{error}</p>
    );

    if (!paths.length) return (
        <p className="w-full text-center text-gray-400 italic mt-6 py-10 border border-dashed border-gray-200 rounded">
            No crawler paths configured for this task. Add paths in the task settings to get started.
        </p>
    );

    return (
        <div className="w-full">
            {paths.map((path) => {
                const pathDoc = pathDocs.find((d) => d.journey_id?.toString() === path._id?.toString()) ?? null;
                return (
                    <PathCard
                        key={path._id}
                        path={path}
                        pathDoc={pathDoc}
                        taskId={taskId}
                        token={token}
                        organizationId={organizationId}
                        onRunComplete={handleRunComplete}
                        onScreenshotClick={handleScreenshotClick}
                        blacklist={varBlacklist}
                        whitelist={varWhitelist}
                    />
                );
            })}

            {screenshotModal.open && (
                <PrimaryModal
                    title={screenshotModal.label}
                    hideModal={() => setScreenshotModal({ open: false, src: "", label: "" })}
                    widthClass="max-w-4xl"
                >
                    <div className="p-4 flex justify-center items-center">
                        <img
                            src={screenshotModal.src}
                            alt={screenshotModal.label}
                            className="max-w-full max-h-[70vh] object-contain rounded"
                        />
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default PathCrawlsTab;
